import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { Button, Flex, Popover, SelectMenu } from '@decisiv/ui-components';
import Calendar from '@decisiv/ui-components/lib/atoms/Calendar';
import iconix from '@decisiv/iconix';
import { hourOptions } from '../../utils/constants';
import { ButtonsRow, HourRow, PopoverContent, TriggerButton } from './style';
import { formatShortDate } from '../../utils/dates';

const DEFAULT_START_HOUR = '00';
const DEFAULT_END_HOUR = '23';

function formatHour(hour) {
  const h = Number(hour);
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:00 ${period}`;
}

function formatLabel(dates, startHour, endHour) {
  if (!dates || !dates[0] || !dates[1]) return '';
  const start = `${formatShortDate(dates[0])} ${formatHour(startHour)}`;
  const end = `${formatShortDate(dates[1])} ${formatHour(endHour)}`;
  return `${start} — ${end}`;
}

export default function DateTimeRangePicker({
  values = undefined,
  startHour = DEFAULT_START_HOUR,
  endHour = DEFAULT_END_HOUR,
  onApply,
  onClear,
  zIndex = 999,
  name,
}) {
  const [visible, setVisible] = useState(false);
  const [draftRange, setDraftRange] = useState(
    values || [undefined, undefined],
  );
  const [draftStartHour, setDraftStartHour] = useState(
    startHour || DEFAULT_START_HOUR,
  );
  const [draftEndHour, setDraftEndHour] = useState(endHour || DEFAULT_END_HOUR);

  const hide = () => setVisible(false);

  // Seed the draft state from the current props each time the popover opens so
  // it reflects the latest values without clobbering an in-progress selection.
  const openPicker = () => {
    setDraftRange(values || [undefined, undefined]);
    setDraftStartHour(startHour || DEFAULT_START_HOUR);
    setDraftEndHour(endHour || DEFAULT_END_HOUR);
    setVisible(true);
  };

  const handleApply = () => {
    onApply(draftRange, draftStartHour, draftEndHour);
    hide();
  };

  const handleClear = () => {
    setDraftRange([undefined, undefined]);
    setDraftStartHour(DEFAULT_START_HOUR);
    setDraftEndHour(DEFAULT_END_HOUR);
    onClear();
    hide();
  };

  const CalendarIcon = iconix.Calendar;
  const label = formatLabel(
    values,
    startHour || DEFAULT_START_HOUR,
    endHour || DEFAULT_END_HOUR,
  );
  const applyDisabled = !draftRange || !draftRange[0] || !draftRange[1];

  return (
    <Popover
      manageEvents={false}
      visible={visible}
      onRequestHide={hide}
      hideOnClickOutside={false}
      hideOnESC
      placement="bottom-start"
      showArrow={false}
      zIndex={zIndex}
      target={({ ref }) => (
        <TriggerButton
          ref={ref}
          type="button"
          name={name}
          onClick={() => (visible ? hide() : openPicker())}
        >
          <CalendarIcon size="small" />
          <span>{label || t`Select date & time range`}</span>
        </TriggerButton>
      )}
    >
      <PopoverContent>
        <Calendar
          variant="range"
          selected={draftRange}
          onChange={setDraftRange}
        />
        <HourRow>
          <Flex marginRight={2}>
            <SelectMenu
              label={t`Start Hour:`}
              name="dtrp-start-hour"
              options={hourOptions}
              selected={draftStartHour}
              onChange={(e) => setDraftStartHour(Array.isArray(e) ? e[0] : e)}
              zIndex={(zIndex || 999) + 1}
            />
          </Flex>
          <Flex>
            <SelectMenu
              label={t`End Hour:`}
              name="dtrp-end-hour"
              options={hourOptions}
              selected={draftEndHour}
              onChange={(e) => setDraftEndHour(Array.isArray(e) ? e[0] : e)}
              zIndex={(zIndex || 999) + 1}
            />
          </Flex>
        </HourRow>
        <ButtonsRow>
          <Button
            text={t`Apply`}
            size="small"
            onClick={handleApply}
            disabled={applyDisabled}
          />
          <Button
            text={t`Cancel`}
            size="small"
            kind="secondary"
            onClick={hide}
          />
          <Button
            text={t`Clear`}
            size="small"
            variant="ghost"
            intent="danger"
            onClick={handleClear}
            style={{ marginLeft: 'auto' }}
          />
        </ButtonsRow>
      </PopoverContent>
    </Popover>
  );
}

DateTimeRangePicker.propTypes = {
  values: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  startHour: PropTypes.string,
  endHour: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  zIndex: PropTypes.number,
  name: PropTypes.string.isRequired,
};
